import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
// A value for <input type="datetime-local"> (local wall-clock time, no zone).
const toLocalInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const MIN_LEAD_MINUTES = 2;

const ScheduleModal = ({ isOpen, onClose, onConfirm, isLoading, campaign }) => {
  const [value, setValue] = useState("");

  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  useEffect(() => {
    if (!isOpen) return;
    // Start from the existing schedule, otherwise tomorrow at 09:00 local time.
    if (campaign?.scheduledAt) {
      setValue(toLocalInput(new Date(campaign.scheduledAt)));
    } else {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      setValue(toLocalInput(d));
    }
  }, [isOpen, campaign]);

  if (!isOpen) return null;

  const picked = value ? new Date(value) : null;
  const valid = picked && !Number.isNaN(picked.getTime());
  const tooSoon = valid && picked.getTime() - Date.now() < MIN_LEAD_MINUTES * 60 * 1000;
  const canSubmit = valid && !tooSoon && !isLoading;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4">
          <h3 className="font-sans text-xl font-bold text-gray-900">
            {campaign?.status === "SCHEDULED" ? "Reschedule" : "Schedule"} Product Update
          </h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 pb-4">
          <p className="mb-4 text-sm text-gray-600">
            <span className="font-semibold">{campaign?.subject}</span> will go to every active,
            verified user who has not unsubscribed, starting at the time you pick.
          </p>
          <label className="block mb-1 text-sm font-semibold text-gray-700" htmlFor="schedule-at">
            Send at
          </label>
          <input
            id="schedule-at"
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="px-3 py-2 w-full text-sm rounded-xl border-2 border-gray-200 focus:outline-none focus:border-[#013941]"
          />
          <p className="mt-2 text-xs text-gray-500">
            Your time zone: {timeZone}.
            {valid && ` That is ${picked.toISOString().replace("T", " ").slice(0, 16)} UTC.`}
          </p>
          {tooSoon && (
            <p className="mt-2 text-xs font-medium text-red-600">
              Pick a time at least {MIN_LEAD_MINUTES} minutes from now.
            </p>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Until it starts you can unschedule it and edit it as a draft.
          </p>
        </div>

        <div className="flex gap-3 justify-end px-6 pb-6">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white rounded-xl border-2 border-gray-200 hover:bg-gray-50 active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(picked.toISOString())}
            disabled={!canSubmit}
            className="px-5 py-2.5 text-sm font-bold text-white bg-[#013941] rounded-xl disabled:opacity-50 hover:bg-[#012B36] active:scale-95"
          >
            {isLoading ? "Scheduling..." : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
